import { createFileRoute } from '@tanstack/react-router'
import React, { useState, useEffect, useMemo } from 'react';
import Layout from '@/components/Layout';
import { apiFetch } from '@/utils/api';
import { useI18n } from '@/hooks/useI18n';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { ClockIcon } from '@heroicons/react/24/outline';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PendingTimeOffRequestsButton } from '@/components/PendingTimeOffRequestsButton';

interface JobHoursSummary {
    job_id: string | null;
    job_title: string;
    hours: number;
    shifts_count: number;
}

interface EmployeeTimesheet {
    employee_id: string;
    first_name: string;
    last_name: string;
    email: string | null;
    total_hours: number;
    paid_time_off_hours: number;
    shifts_count: number;
    time_offs_count: number;
    job_breakdown: JobHoursSummary[];
}

interface TimesheetSummary {
    employees: EmployeeTimesheet[];
    month: string;
    total_hours: number;
    total_pto_hours: number;
}

// Helper functions for week calculations
function getWeekNumber(date: Date): number {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

function getMonday(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
}

function formatDateISO(date: Date): string {
    return date.toISOString().split('T')[0];
}

export default function TimesheetsPage() {
    const { t } = useI18n();
    const [loading, setLoading] = useState(true);
    const [timesheetData, setTimesheetData] = useState<TimesheetSummary | null>(null);
    const [error, setError] = useState<string | null>(null);

    // View mode state
    const [viewMode, setViewMode] = useState<'month' | 'week'>('month');

    // Month selection state
    const currentDate = new Date();
    const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1); // 1-12

    // Week selection state - default to current week's Monday
    const currentMonday = useMemo(() => getMonday(currentDate), []);
    const [selectedWeekStart, setSelectedWeekStart] = useState<Date>(currentMonday);

    // Generate year options (current year and 2 years back)
    const yearOptions = Array.from({ length: 3 }, (_, i) => currentDate.getFullYear() - i);

    // Month options
    const monthOptions = [
        { value: 1, label: 'January' },
        { value: 2, label: 'February' },
        { value: 3, label: 'March' },
        { value: 4, label: 'April' },
        { value: 5, label: 'May' },
        { value: 6, label: 'June' },
        { value: 7, label: 'July' },
        { value: 8, label: 'August' },
        { value: 9, label: 'September' },
        { value: 10, label: 'October' },
        { value: 11, label: 'November' },
        { value: 12, label: 'December' }
    ];

    // Generate week options grouped by month
    const weekOptions = useMemo(() => {
        const weeks: { value: string; label: string; month: number; year: number }[] = [];

        // Generate weeks for the last 12 months
        const startDate = new Date(currentDate);
        startDate.setMonth(startDate.getMonth() - 12);

        let currentWeekStart = getMonday(startDate);
        const endDate = new Date(currentDate);
        endDate.setMonth(endDate.getMonth() + 1);

        while (currentWeekStart <= endDate) {
            const weekEnd = new Date(currentWeekStart);
            weekEnd.setDate(weekEnd.getDate() + 6);

            const weekNum = getWeekNumber(currentWeekStart);
            const year = currentWeekStart.getFullYear();
            const month = currentWeekStart.getMonth() + 1;

            weeks.push({
                value: formatDateISO(currentWeekStart),
                label: `Week ${weekNum} (${formatDateISO(currentWeekStart)} - ${formatDateISO(weekEnd)})`,
                month,
                year
            });

            currentWeekStart = new Date(currentWeekStart);
            currentWeekStart.setDate(currentWeekStart.getDate() + 7);
        }

        return weeks.reverse(); // Most recent first
    }, []);

    // Group weeks by month and year
    const weeksByMonth = useMemo(() => {
        const grouped: { [key: string]: typeof weekOptions } = {};

        weekOptions.forEach(week => {
            const key = `${week.year}-${String(week.month).padStart(2, '0')}`;
            if (!grouped[key]) {
                grouped[key] = [];
            }
            grouped[key].push(week);
        });

        return grouped;
    }, [weekOptions]);

    useEffect(() => {
        fetchTimesheetData();
    }, [selectedYear, selectedMonth, selectedWeekStart, viewMode]);

    const fetchTimesheetData = async () => {
        setLoading(true);
        setError(null);
        try {
            let url: string;

            if (viewMode === 'month') {
                console.log('Fetching timesheet data for month:', selectedYear, selectedMonth);
                url = `/api/timesheets/summary?year=${selectedYear}&month=${selectedMonth}`;
            } else {
                // For week view, calculate the week's date range
                const weekEnd = new Date(selectedWeekStart);
                weekEnd.setDate(weekEnd.getDate() + 6);

                console.log('Fetching timesheet data for week:', formatDateISO(selectedWeekStart), 'to', formatDateISO(weekEnd));

                // Use the month/year from the week start for the API call
                const weekYear = selectedWeekStart.getFullYear();
                const weekMonth = selectedWeekStart.getMonth() + 1;
                url = `/api/timesheets/summary?year=${weekYear}&month=${weekMonth}`;
            }

            const response = await apiFetch(url);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log('Received timesheet data:', data);
            setTimesheetData(data);
        } catch (err: any) {
            console.error('Failed to fetch timesheet data:', err);
            setError(err.message || 'Failed to load timesheet data');
        } finally {
            setLoading(false);
        }
    };

    const formatHours = (hours: number) => {
        return hours.toFixed(2);
    };

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
                            <ClockIcon className="h-8 w-8" />
                            {t('employees.timesheet.subtitle', 'common')}
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            {t('employees.timesheet.description', 'common')}
                        </p>
                    </div>
                </div>

                {/* View Mode Toggle */}
                <div className="flex gap-4 justify-between items-start">
                    <div className="flex gap-4 items-start">
                        <Tabs value={viewMode} onValueChange={(value) => setViewMode(value as 'month' | 'week')}>
                            <TabsList>
                                <TabsTrigger value="month">Month</TabsTrigger>
                                <TabsTrigger value="week">Week</TabsTrigger>
                            </TabsList>
                        </Tabs>

                        {/* Period Selection Filter */}
                        {viewMode === 'month' ? (
                    <div className="flex gap-4 pt-0.5">
                        <Select
                            value={selectedYear.toString()}
                            onValueChange={(value) => setSelectedYear(parseInt(value))}
                        >
                            <SelectTrigger className="w-[120px]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {yearOptions.map(year => (
                                    <SelectItem key={year} value={year.toString()}>
                                        {year}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select
                            value={selectedMonth.toString()}
                            onValueChange={(value) => setSelectedMonth(parseInt(value))}
                        >
                            <SelectTrigger className="w-[150px]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {monthOptions.map(month => (
                                    <SelectItem key={month.value} value={month.value.toString()}>
                                        {month.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                ) : (
                    <div className="flex gap-4 pt-0.5">
                        <Select
                            value={formatDateISO(selectedWeekStart)}
                            onValueChange={(value) => setSelectedWeekStart(new Date(value))}
                        >
                            <SelectTrigger className="w-[400px]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {Object.entries(weeksByMonth).map(([monthKey, weeks]) => {
                                    const [year, month] = monthKey.split('-');
                                    const monthName = monthOptions[parseInt(month) - 1]?.label || month;

                                    return (
                                        <SelectGroup key={monthKey}>
                                            <SelectLabel>{monthName} {year}</SelectLabel>
                                            {weeks.map(week => (
                                                <SelectItem key={week.value} value={week.value}>
                                                    {week.label}
                                                </SelectItem>
                                            ))}
                                        </SelectGroup>
                                    );
                                })}
                            </SelectContent>
                        </Select>
                    </div>
                )}
                    </div>

                    <PendingTimeOffRequestsButton />
                </div>
                {/* Error State */}
                {error && (
                    <Card className="border-destructive">
                        <CardContent className="pt-6">
                            <p className="text-destructive">{error}</p>
                        </CardContent>
                    </Card>
                )}

                {/* Loading State */}
                {loading && (
                    <Card>
                        <CardContent className="pt-6">
                            <div className="space-y-2">
                                <Skeleton className="h-4 w-full" />
                                <Skeleton className="h-4 w-full" />
                                <Skeleton className="h-4 w-full" />
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Timesheet Table */}
                {!loading && timesheetData && timesheetData.employees && timesheetData.employees.length > 0 && (
                    <div className="">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('employees.timesheet.TotalHours', 'common')}</TableHead>
                                    <TableHead>{t('employees.timesheet.employee', 'common')}</TableHead>
                                    <TableHead className="text-right">{t('employees.timesheet.shift', 'common')}</TableHead>
                                    <TableHead className="text-right">{t('employees.timesheet.TotalHours', 'common')}</TableHead>
                                    <TableHead className="text-right">{t('employees.timesheet.PTODays', 'common')}</TableHead>
                                    <TableHead className="text-right">{t('employees.timesheet.PTOHours', 'common')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {timesheetData.employees.map((employee) => (
                                    <React.Fragment key={employee.employee_id}>
                                        <TableRow>
                                            <TableCell className="font-medium">
                                                {employee.first_name} {employee.last_name}
                                            </TableCell>
                                            <TableCell className="text-muted-foreground">
                                                {employee.email || '-'}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {employee.shifts_count}
                                            </TableCell>
                                            <TableCell className="text-right font-medium">
                                                {formatHours(employee.total_hours)}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {employee.time_offs_count}
                                            </TableCell>
                                            <TableCell className="text-right font-medium">
                                                {formatHours(employee.paid_time_off_hours)}
                                            </TableCell>
                                        </TableRow>
                                        {/* Job breakdown rows */}
                                        {employee.job_breakdown && employee.job_breakdown.length > 0 && (
                                            employee.job_breakdown.map((job, idx) => (
                                                <TableRow key={`${employee.employee_id}-job-${idx}`} className="bg-muted/30">
                                                    <TableCell className="pl-8 text-sm text-muted-foreground" colSpan={2}>
                                                        ↳ {job.job_title}
                                                    </TableCell>
                                                    <TableCell className="text-right text-sm text-muted-foreground">
                                                        {job.shifts_count}
                                                    </TableCell>
                                                    <TableCell className="text-right text-sm text-muted-foreground">
                                                        {formatHours(job.hours)}
                                                    </TableCell>
                                                    <TableCell colSpan={2}></TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                    </div>

                )}

                {/* Empty State */}
                {!loading && timesheetData && timesheetData.employees && timesheetData.employees.length === 0 && (
                    <Card>
                        <CardContent className="pt-6 text-center">
                            <ClockIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                            <p className="text-muted-foreground">
                                {viewMode === 'month' ? (
                                    <>No timesheet data found for {monthOptions.find(m => m.value === selectedMonth)?.label} {selectedYear}</>
                                ) : (
                                    <>No timesheet data found for week starting {formatDateISO(selectedWeekStart)}</>
                                )}
                            </p>
                            <p className="text-sm text-muted-foreground mt-2">
                                Make sure to sync time activities from Connecteam first.
                            </p>
                        </CardContent>
                    </Card>
                )}
            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/people/timesheets')({
    component: TimesheetsPage,
})
